import { api } from '../lib/api'

export type CreatePostPayload = {
  title: string
  subtitle?: string
  format?: 'micro' | 'zine' | 'book' | 'event' | 'job'
  category?: string
  micro_text?: string
  micro_template_id?: string
  cover_image_url?: string
  cover_landscape_url?: string
  body_plain?: string
  access_level?: 'FREE' | 'SUBSCRIBERS_ONLY' | 'PAY_PER_VIEW' | 'SUBSCRIBERS_OR_PPV'
  ppv_price_pixe?: number | null
  required_tier_rank?: number | null
  // Publishing controls / metadata (optional)
  scheduled_at?: string | null
  audience?: 'everyone' | 'free' | 'paid'
  email_subject?: string
  send_email_notification?: boolean
  share_to_twitter?: boolean
  tweet_text?: string
  url_slug_override?: string
  canonical_url?: string
  comments_enabled?: boolean
  notes_enabled?: boolean
  comments_subscriber_only?: boolean
  cover_alt_text?: string
  is_local?: boolean
  local_city?: string
  local_region?: string
  local_country?: string
  local_lat?: number | null
  local_lng?: number | null
  local_radius_km?: number | null
  event_starts_at?: string | null
  event_ends_at?: string | null
  event_venue_name?: string
  event_address?: string
  event_ticket_price_pixe?: number | null
  event_capacity?: number | null
  event_is_ticketed?: boolean
  event_is_cancelled?: boolean
  event_sales_closed?: boolean
  tickets_sold?: number
  tickets_remaining?: number | null
  viewer_has_ticket?: boolean
  viewer_ticket_count?: number
  viewer_ticket_receipt_hash?: string | null
  viewer_ticket_receipt_status?: string | null
  viewer_ticket_receipt_tx_hash?: string | null
}

export type Post = {
  id: number
  title: string
  subtitle?: string
  format?: 'micro' | 'zine' | 'book' | 'event' | 'job' | null
  category?: string | null
  category_name?: string | null
  micro_text?: string | null
  micro_template_id?: string | null
  views_count?: number
  has_proof?: boolean
  receipt_id?: string | number | null
  chain_receipt_id?: string | number | null
  receipt_status?: 'pending' | 'confirmed' | 'failed' | string | null
  receipt_download_url?: string | null
  receipt_qr_url?: string | null
  cover_image_url?: string
  cover_landscape_url?: string
  access_level?: 'FREE' | 'SUBSCRIBERS_ONLY' | 'PAY_PER_VIEW' | 'SUBSCRIBERS_OR_PPV'
  ppv_price_pixe?: number | null
  required_tier_rank?: number | null
  share_url?: string
  send_email_notification?: boolean
  is_local?: boolean
  local_city?: string
  local_region?: string
  local_country?: string
  local_lat?: number | null
  local_lng?: number | null
  local_radius_km?: number | null
  event_starts_at?: string | null
  event_ends_at?: string | null
  event_venue_name?: string
  event_address?: string
  event_ticket_price_pixe?: number | null
  event_capacity?: number | null
  event_is_ticketed?: boolean
  event_is_cancelled?: boolean
  event_sales_closed?: boolean
  tickets_sold?: number
  tickets_remaining?: number | null
  viewer_has_ticket?: boolean
  viewer_ticket_count?: number
  viewer_ticket_receipt_hash?: string | null
  viewer_ticket_receipt_status?: string | null
  viewer_ticket_receipt_tx_hash?: string | null
  fee_status?: string
  publish_status?: string
  last_error?: string
  moderation_state?: 'draft' | 'pending_review' | 'blocked' | 'published' | string
  moderation_checked_at?: string | null
}

export type PostReceipt = {
  content_hash?: string
  chain_tx_id?: string
  chain_block?: number | null
  verified_at?: string | null
  status?: string | null
  explorer_url?: string | null
  receipt_id?: string | null
  receipt_download_url?: string | null
  receipt_status?: 'pending' | 'confirmed' | 'failed' | string | null
}

export type PublishPreflightResponse = {
  status: 'ok' | 'warn' | 'fail'
  checks: Array<{
    name: string
    status: 'ok' | 'warn' | 'fail'
    message: string
    latency_ms?: number
    details?: Record<string, any>
  }>
}

export type PublishReadinessPreflightResponse = {
  ok: boolean
  blocking: Array<{ code: string; message: string }>
  warnings: Array<{ code: string; message: string }>
  capabilities: {
    wallet_ready: boolean
    anchoring_enabled: boolean
    chain_reachable: boolean
  }
}

export async function createPost(payload: CreatePostPayload, opts?: { idempotencyKey?: string }) {
  // Publishing can trigger on-chain anchoring; allow extra time.
  const headers: Record<string, string> = {}
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey
  const { data } = await api.post<Post>('/api/posts/', payload, { timeout: 60000, headers })
  return data
}

export async function preflightPublish(payload: Partial<CreatePostPayload>) {
  const { data } = await api.post<PublishPreflightResponse>('/api/posts/preflight/', payload, { timeout: 20000 })
  return data
}

export async function preflightPublishReadiness(payload: Partial<CreatePostPayload>) {
  try {
    const { data } = await api.post<PublishReadinessPreflightResponse>('/api/posts/publish-preflight/', payload, { timeout: 12000 })
    return data
  } catch (err: any) {
    const status = Number(err?.response?.status || 0)
    if (status === 404 || status === 405) {
      const legacy = await preflightPublish(payload)
      const isFail = String(legacy?.status || '').toLowerCase() === 'fail'
      const checks = Array.isArray(legacy?.checks) ? legacy.checks : []
      return {
        ok: !isFail,
        blocking: isFail
          ? checks
              .filter((c: any) => String(c?.ok).toLowerCase() === 'false')
              .map((c: any) => ({ code: String(c?.code || 'preflight_failed'), message: String(c?.message || 'Publish preflight failed.') }))
          : [],
        warnings: [],
        capabilities: {
          wallet_ready: true,
          anchoring_enabled: true,
          chain_reachable: true,
        },
      }
    }
    throw err
  }
}

export async function updatePostBodyPlain(id: number | string, plain: string) {
  const { data } = await api.patch<Post>(`/api/posts/${id}/body/`, { plain })
  return data
}

export async function updatePostBodyJson(id: number | string, json: any) {
  const { data } = await api.patch<Post>(`/api/posts/${id}/body/`, { json })
  return data
}

export async function patchPostBody(id: number | string, payload: { plain?: string; json?: any; html?: string }) {
  const { data } = await api.patch<Post>(`/api/posts/${id}/body/`, payload)
  return data
}

export type PresignResponse = {
  upload_url: string
  public_url: string
  key: string
  content_type: string
}

export type UploadModerationFinalizeResponse = {
  public_url: string
  preview_url?: string | null
  render_url?: string | null
  key: string
  moderation_status?: 'passed' | 'held' | 'blocked' | 'error' | 'pending'
  moderation_decision?: 'auto_approve' | 'hold_for_review' | 'block'
  check_id?: string
}

export type UploadModerationContext = {
  upload_content_type?: 'post_image' | 'listing_image' | 'profile_image' | 'page_image' | 'avatar_image' | string
  target_field?: string
  object_type?: string
  object_id?: string | number
}

export async function presignCover(filename: string, contentType?: string) {
  const { data } = await api.post<PresignResponse>('/api/posts/uploads/presign/', {
    filename,
    content_type: contentType,
    kind: 'post_cover',
  })
  return data
}

export async function uploadToSignedUrl(uploadUrl: string, file: File, contentType: string) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'Cache-Control': 'public, max-age=31536000, immutable' },
    body: file,
  })
  if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`)
}

export async function finalizePresignedCoverUpload(
  payload: { public_url: string; key: string; content_type?: string } & UploadModerationContext
) {
  const { data } = await api.post<UploadModerationFinalizeResponse>('/api/posts/uploads/finalize/', payload)
  return data
}

export async function uploadCoverDirect(file: File, context?: UploadModerationContext) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('filename', file.name)
  if (context?.upload_content_type) fd.append('upload_content_type', String(context.upload_content_type))
  if (context?.target_field) fd.append('target_field', String(context.target_field))
  if (context?.object_type) fd.append('object_type', String(context.object_type))
  if (context?.object_id !== undefined && context?.object_id !== null) fd.append('object_id', String(context.object_id))
  const { data } = await api.post<{
    public_url: string
    preview_url?: string | null
    render_url?: string | null
    key: string
    content_type: string
    moderation_status?: 'passed' | 'held' | 'blocked' | 'error' | 'pending'
    moderation_decision?: 'auto_approve' | 'hold_for_review' | 'block'
    check_id?: string
  }>(
    '/api/posts/uploads/direct/',
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return data
}

// ---- Social actions (mirror mobile contracts) ----

export async function likePost(id: number | string): Promise<{ ok: boolean; liked: boolean; likes: number }> {
  const { data } = await api.post(`/api/posts/${id}/like/`)
  return data
}

export async function unlikePost(id: number | string): Promise<{ ok: boolean; liked: boolean; likes: number }> {
  const { data } = await api.post(`/api/posts/${id}/unlike/`)
  return data
}

export type PostSaveResponse = { ok?: boolean; saved: boolean; saves?: number }

export async function savePost(id: number | string): Promise<PostSaveResponse> {
  try {
    const { data } = await api.post(`/api/posts/${id}/bookmark/`)
    return data
  } catch (err: any) {
    if (err?.response?.status === 404 || err?.response?.status === 405) {
      const { data } = await api.post(`/api/posts/${id}/save/`)
      return data
    }
    throw err
  }
}

export async function unsavePost(id: number | string): Promise<PostSaveResponse> {
  try {
    const { data } = await api.delete(`/api/posts/${id}/bookmark/`)
    return data
  } catch (err: any) {
    if (err?.response?.status === 404 || err?.response?.status === 405) {
      const { data } = await api.delete(`/api/posts/${id}/save/`)
      return data
    }
    throw err
  }
}

export async function sharePost(id: number | string): Promise<{ ok: boolean; shares: number }> {
  const { data } = await api.post(`/api/posts/${id}/share/`)
  return data
}

export async function sendPostPixeCardEmail(params: {
  postId: number | string
  email: string
  subject?: string
}): Promise<{ status: string; message_id?: string }> {
  const { data } = await api.post('/api/cards/send_email/', {
    entity_type: 'post',
    entity_reference: String(params.postId),
    email: params.email,
    subject: params.subject,
  })
  return data
}

export async function recordPostView(id: number | string): Promise<{ ok?: boolean; views_count?: number } | null> {
  try {
    const { data } = await api.post(`/api/posts/${id}/view/`)
    return data
  } catch (err: any) {
    // Some environments may not expose the endpoint yet.
    if (err?.response?.status === 404 || err?.response?.status === 405) return null
    throw err
  }
}

export type TicketPurchaseResponse = {
  ok: boolean
  order: {
    id: number
    status: 'paid' | 'pending' | 'failed' | 'refunded' | string
    qty: number
    price_pixe_each: number
    total_pixe: number
    fee_pixe: number
    net_pixe: number
  }
  tickets: {
    count: number
    serials: string[]
  }
  ticket_qr_payloads?: Array<Record<string, any>>
  receipt_hash?: string
  receipt_status?: string
  poe_tx_hash?: string
  poe_block_number?: number | null
  total_pixe: number
  fee_pixe: number
  net_pixe: number
  distribution_account_id?: number | null
  ticket_email_queued?: boolean
  tickets_sold: number
  tickets_remaining: number
  viewer_has_ticket: boolean
  viewer_ticket_count: number
}

export async function purchaseEventTicket(postId: number | string, qty = 1) {
  const { data } = await api.post<TicketPurchaseResponse>(`/api/events/${postId}/tickets/purchase/`, { qty })
  return data
}

export async function resendTicketOrderEmail(orderId: number | string) {
  const { data } = await api.post(`/api/tickets/order/${orderId}/resend-email/`)
  return data
}

export async function getPostReceipt(id: number | string): Promise<PostReceipt | null> {
  try {
    const { data } = await api.get<PostReceipt>(`/api/posts/${id}/receipt/`)
    return data
  } catch (err: any) {
    if (err?.response?.status === 404 || err?.response?.status === 405) return null
    throw err
  }
}
